import { Injectable, Injector, effect, inject, runInInjectionContext } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from './auth.service';
import { ChatApiService, VISITOR_CHAT_STORAGE } from './chat-api.service';
import { ChatRealtimeService } from './chat-realtime.service';
import { ChatSoundService } from './chat-sound.service';
import { MODULE_LIVE_CHAT, ModulesService } from './modules.service';
import { PushNotificationService } from './push-notification.service';

/**
 * Keeps admin SignalR + push + unread badge alive on every route
 * (admin panel and storefront), not only while AdminLayout is mounted.
 */
@Injectable({ providedIn: 'root' })
export class AdminChatSessionService {
  private readonly auth = inject(AuthService);
  private readonly modules = inject(ModulesService);
  private readonly chatApi = inject(ChatApiService);
  private readonly chatRealtime = inject(ChatRealtimeService);
  private readonly push = inject(PushNotificationService);
  private readonly chatSound = inject(ChatSoundService);
  private readonly injector = inject(Injector);

  private lastBusinessKey: string | null = null;
  private bootstrapped = false;
  private runId = 0;

  constructor() {
    this.chatSound.armUnlock();

    runInInjectionContext(this.injector, () => {
      effect(() => {
        const loggedIn = this.auth.isLoggedIn();
        const canChat = this.auth.canUseChat() && this.modules.isEnabled(MODULE_LIVE_CHAT);
        // Track module map so effect re-runs when tenant modules load/change.
        void this.modules.modules();
        const businessKey = this.auth.businessKey();

        if (!loggedIn) {
          void this.teardown();
          return;
        }

        const businessChanged =
          this.bootstrapped && businessKey != null && businessKey !== this.lastBusinessKey;
        if (businessChanged) {
          this.chatRealtime.setUnread(0);
        }
        void this.ensureStarted(canChat, businessChanged);
      });
    });

    // After SignalR recovery: one unread REST sync (no permanent polling).
    this.chatRealtime.adminSyncNeeded$.subscribe(() => {
      if (!this.bootstrapped || !this.canOperateChat()) {
        return;
      }
      const id = this.runId;
      this.chatApi.unreadCount(true).subscribe({
        next: (u) => {
          if (id === this.runId) {
            this.chatRealtime.setUnread(u.count);
          }
        },
        error: () => undefined
      });
    });
  }

  /** Call after login / refreshMe when session is known good. */
  async refresh(): Promise<void> {
    if (!this.auth.isLoggedIn()) {
      await this.teardown();
      return;
    }
    // Do not force reconnect — concurrent stop+start aborts SignalR negotiation
    // ("connection was stopped during negotiation"). Business switches go through
    // the effect with reconnect=true when the key actually changes.
    await this.ensureStarted(this.canOperateChat(), false);
  }

  async stop(): Promise<void> {
    await this.teardown();
  }

  private canOperateChat(): boolean {
    return this.auth.canUseChat() && this.modules.isEnabled(MODULE_LIVE_CHAT);
  }

  private async ensureStarted(canChat: boolean, reconnect: boolean): Promise<void> {
    const id = ++this.runId;
    this.chatSound.armUnlock();

    await this.discardVisitorSession();
    if (id !== this.runId) {
      return;
    }

    if (!canChat) {
      await this.teardownAdminOnly();
      return;
    }

    if (reconnect) {
      await this.chatRealtime.stopAdmin();
      if (id !== this.runId) {
        return;
      }
      this.bootstrapped = false;
    }

    if (this.bootstrapped && this.chatRealtime.connected() && !reconnect) {
      return;
    }

    this.chatApi.unreadCount(true).subscribe({
      next: (u) => {
        if (id === this.runId) {
          this.chatRealtime.setUnread(u.count);
        }
      },
      error: () => {
        if (id === this.runId) {
          this.chatRealtime.setUnread(0);
        }
      }
    });

    await this.chatRealtime.startAdmin();
    if (id !== this.runId) {
      return;
    }

    await this.push.registerForAdmin();
    if (id !== this.runId) {
      return;
    }

    this.lastBusinessKey = this.auth.businessKey();
    this.bootstrapped = true;
  }

  /**
   * An admin logging in on the storefront must not keep the visitor engage
   * (timer, hub, idle conversation, notification badge).
   */
  private async discardVisitorSession(): Promise<void> {
    await this.chatRealtime.stopVisitor();

    const visitorKey = localStorage.getItem(VISITOR_CHAT_STORAGE.visitorKey);
    const conversationId = localStorage.getItem(VISITOR_CHAT_STORAGE.conversationId);
    localStorage.removeItem(VISITOR_CHAT_STORAGE.visitorKey);
    localStorage.removeItem(VISITOR_CHAT_STORAGE.conversationId);

    if (!visitorKey) {
      return;
    }

    try {
      await firstValueFrom(
        this.chatApi.visitorAbandon({
          visitorKey,
          conversationId
        })
      );
    } catch {
      /* Best-effort: login must not fail if abandon is unavailable. */
    }
  }

  private async teardown(): Promise<void> {
    this.runId++;
    await this.teardownAdminOnly();
  }

  private async teardownAdminOnly(): Promise<void> {
    await this.chatRealtime.stopAdmin();
    this.chatRealtime.setUnread(0);
    this.bootstrapped = false;
    this.lastBusinessKey = null;
  }
}
